import {Rectangle} from "pixi.js";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {Label} from "../../shared-2d/Label";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {HardwareItem} from "../../../../../../api/Hardware";
import {placedOnLeaf} from "../hardware/hardwarePreps";
import {doorEdgeOf, ruleForItem} from "../../model/hardware/MdHardwareRules";
import {hardwareGlyphFor} from "../../shared-2d/hardwareGlyphs";
import {drawCutMarker} from "../../shared-2d/cutHatch";
import {drawDoorSweepEdge, hasSweep, SWEEP_FACE} from "../../shared-2d/DoorSweep";
import {drawDoorKickPlateEdge, hasKickPlate, KICK_PLATE_FACE} from "../../shared-2d/DoorKickPlate";
import {lineBlueHeavy, panelMetalGrey} from "../../const";
import {EdgeHardwareThing, EdgeRect} from "./EdgeHardwareThing";
import {EdgeHead} from "../../shared-2d/hardwareGlyphs/HardwareGlyph";
import {doorFaceOf, faceSide, getProfileObj, getSwingSide, pinDirection, TProfile} from "../frameProfile/Helpers";

// the caption over the edge, matching the face captions (see LocationsView).
const CAPTION_FONT = 60;

// The latch edge of a door leaf, drawn between the Locations view's two faces so the door
// reads folded out: front face | latch edge | back face. The edge is the door thickness
// wide and the leaf tall, on the same vertical origin as the faces (top of the frame jamb
// at y=0, the overcut below it), so hardware heights line up across the three columns.
// Every door placement on this leaf shows here (both faces share the edge) as an
// EdgeHardwareThing: the latch and deadbolt faceplates cut into the edge, face-mounted
// hardware standing off its face. A frame placement on the head (an operator header)
// shows too when its glyph has an edge drawing, seated on the head's face.
//
// The frame head sits over the edge in section, so the view shows how the leaf seats
// in the head's rabbet against the stop. The profile runs round the frame, so the jamb's
// plan section (see scenes/frameProfile) serves as the head's: it's turned so the
// opening direction runs down the edge and the jamb depth across it, with location one
// on whichever side the front face is (the fold-out's left).
export class LatchEdgeView extends ChildView {
    active: boolean;

    private caption: Label;
    // the caption's clearance above the content, shared with the faces.
    private captionGap: number;

    // the edge rect + hardware, positioned by the scene layout. The top of the frame
    // jamb sits at its local origin, with the edge's left (front) face at x=0.
    parentThing: DrawFrmThing;
    private edge: DrawFrmThing;

    private hardwareThings: EdgeHardwareThing[] = [];

    // the head profile in section, and the leaf's swing it was drawn for (which rabbet
    // the leaf sits in).
    private profile: TProfile;
    private swing: "in" | "out";

    constructor(elev: ElevationApp, add: AddFx, active: boolean, captionGap: number) {
        super(elev, add);
        this.active = active;
        this.captionGap = captionGap;

        // the profile's cut dimensions belong to the frame profile view, not here.
        this.dimensionMode.emit(false);

        this.caption = new Label({text: "Door Latch", fontSize: CAPTION_FONT, color: lineBlueHeavy, anchorX: 0.5, anchorY: 1});
        this.add(this.caption);

        this.parentThing = new DrawFrmThing();
        this.add(this.parentThing);

        this.edge = new DrawFrmThing();
        this.parentThing.add(this.edge);

        // clicking the bare edge clears the selection, like clicking a leaf face.
        this.edge.makeInteractive({
            onClick: () => {
                if (this.elev.draw.isPanSuspended()) return;
                this.elev.viewSettings.clearSelection();
            },
        });

        const relayout = () => {
            this.redraw();
            this.elev.draw.currentScene?.applyLayout();
        };

        // a no-swing handing has no side to seat on; draw it as swinging in.
        let direction: "in" | "out" | "both";
        try {
            direction = getSwingSide(elev, active).direction;
        } catch {
            direction = "in";
        }
        this.swing = pinDirection(direction);

        this.profile = getProfileObj({
            elev,
            view: this,
            side: "left",
            direction,
            detail: elev.frameSpec.doorFrameProfile,
            jambDepth: elev.frameSpec.jambDepth,
            jambFace: elev.frameSpec.jambFace,
        });
        // plan -> edge: the opening direction (plan x) runs down the edge, the wall depth
        // (plan y) across it. Location one is at the bottom of the plan section; a
        // front face seen from location two puts it on the right instead.
        this.profile.graphic.rotation = Math.PI / 2;
        this.profile.graphic.scale.y = this.frontIsLocationOne() ? 1 : -1;
        // over the edge, so the profile reads across the leaf top.
        this.parentThing.add(this.profile);

        // the shape re-solves when a profile cut changes; follow its anchor.
        this.trackSubscription(this.profile.changed.subscribe(() => relayout()));

        this.buildHardwareItems();
        this.trackSubscription(elev.hardware.listChanged.subscribe(() => {
            this.buildHardwareItems();
            this.redraw();
        }));

        this.trackSubscription(elev.doorSpec.actualHeight.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.doorThickness.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.overcut.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.undercut.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.topGap.subscribe(relayout));
        this.trackSubscription(elev.frameSpec.frameStopToDoorGap.subscribe(relayout));

        this.redraw();
    }

    private frontIsLocationOne(): boolean {
        return faceSide(this.elev, "front") === "one";
    }

    private edgeRect(): EdgeRect {
        return {x0: 0, x1: this.elev.doorSpec.doorThickness.get(), head: this.headRect()};
    }

    // the placed head profile's extents in edge coords: its local bounds through the
    // turn placeProfile describes. The faces are at the wall side of the plan section
    // (its low x), so the frame top is there and the face return runs a jamb face down.
    private headRect(): EdgeHead | undefined {
        const lb = this.profile.graphic.getLocalBounds();
        if (lb.width <= 0 || lb.height <= 0) return undefined;
        const pos = this.profile.graphic.position;
        const sy = this.profile.graphic.scale.y;
        const xa = pos.x - sy * lb.y;
        const xb = pos.x - sy * (lb.y + lb.height);
        const top = pos.y + lb.x;
        // the rabbet anchor's plan x lands on the edge's y through the same turn.
        const soffit = pos.y + this.profile.primaryDoorAnchor.getLocalX();
        return {x0: Math.min(xa, xb), x1: Math.max(xa, xb), top, faceBottom: top + this.elev.frameSpec.jambFace.get(), soffit};
    }

    // (re)build one edge drawing per door placement on this leaf and per head placement
    // with an edge drawing.
    private buildHardwareItems() {
        for (const t of this.hardwareThings) {
            if (!t.destroyed) t.destroy();
        }
        this.hardwareThings = [];

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        for (const placement of this.elev.hardware.list) {
            const item = hw.find(h => h.product === placement.productId);
            if (!item) continue;
            if (!placedOnLeaf(placement, this.active) && !this.drawnOnHead(placement, item)) continue;
            // an item that lives in the hinge edge (a power transfer) shows there, not here.
            if (placement.target === "door" && doorEdgeOf(item) === "hinge") continue;

            const thing = new EdgeHardwareThing({
                elev: this.elev,
                item: placement,
                product: item,
                edge: () => this.edgeRect(),
                frontFace: () => doorFaceOf(this.elev, "front", this.active),
                onSelect: (additive) => this.selectHardwareItem(placement, additive),
            });
            this.edge.lines.addChild(thing.graphic);
            this.hardwareThings.push(thing);
        }
    }

    // a frame placement the rules put on the head, whose glyph can draw it from the edge.
    private drawnOnHead(placement: MdHardwarePlacement, item: HardwareItem): boolean {
        if (placement.target !== "frame") return false;
        if (ruleForItem(item)?.frameStick !== "top") return false;
        const glyph = hardwareGlyphFor(item);
        return !!glyph && glyph.targets.includes("frame") && !!glyph.drawEdge;
    }

    private selectHardwareItem(item: MdHardwarePlacement, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "hardware-item", id: item.id, kind: item.target, productId: item.productId}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    redraw() {
        const door = this.elev.doorSpec;
        const thickness = door.doorThickness.get();
        const height = door.actualHeight.get();
        const overcut = door.overcut.get();
        const undercut = door.undercut.get();

        const g = this.edge.lines;
        g.clear();

        g.lineStyle(2, lineBlueHeavy, 1);
        g.beginFill(panelMetalGrey, 1);
        g.drawRect(0, overcut, thickness, height);
        g.endFill();

        // the trimmed regions, as the faces show them.
        if (overcut > 0) drawCutMarker(g, 0, 0, thickness, overcut);
        if (undercut > 0) drawCutMarker(g, 0, overcut + height, thickness, undercut);

        // the cosmetic hardware standing off its face at the bottom (as the faces draw it):
        // the kick plate and the sweep. The front face is at x=0; an item sits on the back
        // when the face it mounts on is the back.
        if (!this.elev.viewSettings.frameOnly()) {
            const items = this.elev.hardwareGroup?.hardware ?? [];
            const front = doorFaceOf(this.elev, "front", this.active);
            const faceOf = (mount: string): {x: number; out: 1 | -1} => {
                const onBack = front !== null && front !== mount;
                return {x: onBack ? thickness : 0, out: onBack ? 1 : -1};
            };
            if (hasKickPlate(items)) {
                const f = faceOf(KICK_PLATE_FACE);
                drawDoorKickPlateEdge(g, f.x, f.out, overcut + height, undercut);
            }
            if (hasSweep(items)) {
                const f = faceOf(SWEEP_FACE);
                drawDoorSweepEdge(g, f.x, f.out, overcut + height);
            }
        }

        // the profile first: the hardware that seats on the head reads its placed extents.
        this.placeProfile();

        for (const t of this.hardwareThings) {
            t.draw();
        }
    }

    // seat the leaf top in the profile's rabbet: the leaf corner against the stop sits
    // the head gap below the rabbet anchor and the stop gap off the stop, on the side
    // the leaf swings away from (as linkProfileAndDoor seats a door at a jamb). The
    // profile's graphic is turned (see the constructor), so the plan offsets go through
    // that turn to land in edge coords.
    private placeProfile() {
        const door = this.elev.doorSpec;
        const thickness = door.doorThickness.get();
        const headGap = door.topGap.get() + door.overcut.get();
        const stopGap = this.elev.frameSpec.frameStopToDoorGap.get();

        // the leaf corner the profile seats, in edge coords: at the leaf top, on the
        // location-two side for a leaf swinging out (toward location one), else the
        // location-one side.
        const locationOneX = this.frontIsLocationOne() ? 0 : thickness;
        const cornerX = this.swing === "out" ? thickness - locationOneX : locationOneX;
        const cornerY = door.overcut.get();

        // the corner's offset from the rabbet anchor in the profile's own (plan) coords.
        const px = this.profile.primaryDoorAnchor.getLocalX() + headGap;
        const py = this.profile.primaryDoorAnchor.getLocalY() + (this.swing === "in" ? -stopGap : stopGap);

        // the turn maps plan (x, y) to edge (-sy * y, x).
        const sy = this.profile.graphic.scale.y;
        this.profile.graphic.position.set(cornerX + sy * py, cornerY - px);
    }

    // the extent of the edge and its hardware in parentThing's local (mm) space.
    contentBounds(): Rectangle {
        return this.parentThing.graphic.getLocalBounds();
    }

    // put the edge origin at `x` in the container this view was added to, and centre the
    // caption over the content.
    placeAt(x: number) {
        const b = this.contentBounds();
        this.parentThing.graphic.position.set(x, 0);
        this.caption.graphic.position.set(x + b.x + b.width / 2, b.y - this.captionGap);
    }

    destroy() {
        for (const t of this.hardwareThings) {
            if (!t.destroyed) t.destroy();
        }
        this.hardwareThings = [];
        super.destroy();
    }
}
